import {DeepPartial} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {useGetNoteQuery} from '@dms/api/core';
import {RentalOrderResponseBody} from '@dms/api/rental';
import {EntityResourceIds} from '@dms/api/shared';
import {RentalVehicleResponseBody} from '@dms/api/vehicle';
import {useDocumentTemplates} from '@dms/shared';

import {Nullish, parseDate, useRequiredParams} from 'shared/web';

import {DEFAULT_BATTERY_CHARGE} from '../../../../../constants/DEFAULT_BATTERY_CHARGE';
import {RentalOrderNodeContext} from '../../../../../constants/RentalOrderNodeContext';
import {parseBatteryCharge} from '../../../utils/parseBatteryCharge';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

// eslint-disable-next-line complexity -- Pre-existing violation
export const useGetReturnVehicleFormDefaultValues = (
  rentalOrder: RentalOrderResponseBody | Nullish,
  rentalVehicle: RentalVehicleResponseBody | Nullish
) => {
  const {id: rentalOrderId} = useRequiredParams();
  const hasOrderReturnVehicle = isNotNil(rentalOrder?.returnVehicle);
  const [getTemplates, {isLoading: isLoadingTemplates}] = useDocumentTemplates();

  const {primaryTemplate} = getTemplates('workshop-rental_order_return');

  const {data: rentalOrderNote, isLoading: isLoadingRentalOrderNote} = useGetNoteQuery({
    recordId: rentalOrderId,
    resourceId: EntityResourceIds.rentalOrder,
    context: RentalOrderNodeContext.returnVehicleNote,
  });
  const {data: vehicleConditionNote, isLoading: isLoadingVehicleConditionNote} = useGetNoteQuery({
    recordId: rentalOrderId,
    resourceId: EntityResourceIds.rentalOrder,
    context: RentalOrderNodeContext.returnVehicleConditionNote,
  });

  const isLoading = isLoadingRentalOrderNote || isLoadingVehicleConditionNote || isLoadingTemplates;

  const returnVehicle = rentalOrder?.returnVehicle;
  const returnVehicleRentPrice = returnVehicle?.rentPrice;

  const rentVehicle = rentalOrder?.rentVehicle;
  const rentVehicleRentPrice = rentVehicle?.rentPrice;

  const rentalOrderReturnValues: DeepPartial<ReturnVehicleFormValues> | null = isNotNil(
    returnVehicle
  )
    ? {
        authorizedPersons: returnVehicle.authorizedPersons,
        actualMileage: returnVehicle.actualMileage,
        fuelTank: parseFloat(returnVehicle.fuelTank ?? '0.5'),
        batteryCharge:
          parseBatteryCharge(returnVehicle.batteryCharge ?? rentVehicle?.batteryCharge) ??
          DEFAULT_BATTERY_CHARGE,
        isAddressSame: isNil(returnVehicle.returnAddress),
        rentAddress: returnVehicle.rentAddress,
        rentType: returnVehicle.rentType,
        rentalOrderNote: rentalOrderNote?.text,
        conditionNote: vehicleConditionNote?.text,
        equipment: returnVehicle.equipment,
        actualReturnDate: returnVehicle.rentalPeriod.to
          ? parseDate(returnVehicle.rentalPeriod.to)
          : undefined,
        usedPriceGroup: {
          rentalPriceGroupId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId,
          rentalPriceGroupItemId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId,
        },
        isCustomPriceEnabled: isNotNil(returnVehicle.customRentPrice),
        rentPrice: {
          dailyRate: parseFloat(returnVehicleRentPrice?.dailyRate ?? '0'),
          paidDays: returnVehicleRentPrice?.paidDays,
          freeDailyKilometers: returnVehicleRentPrice?.freeDailyKilometers,
          totalFreeKilometers: returnVehicleRentPrice?.totalFreeKilometers,
          rentalPrice: {
            vatType: returnVehicleRentPrice?.rentalPrice?.vatType ?? 'S',
            withoutVat: parseFloat(returnVehicleRentPrice?.rentalPrice?.withoutVat ?? '0'),
            vat: parseFloat(returnVehicleRentPrice?.rentalPrice?.vat ?? '0'),
            withVat: parseFloat(returnVehicleRentPrice?.rentalPrice?.withVat ?? '0'),
          },
          pricePerKilometer: {
            vatType: returnVehicleRentPrice?.pricePerKilometer?.vatType ?? 'S',
            withoutVat: parseFloat(returnVehicleRentPrice?.pricePerKilometer?.withoutVat ?? '0'),
            vat: parseFloat(returnVehicleRentPrice?.pricePerKilometer?.vat ?? '0'),
            withVat: parseFloat(returnVehicleRentPrice?.pricePerKilometer?.withVat ?? '0'),
          },
          deliveryPrice: {
            vatType: returnVehicleRentPrice?.deliveryPrice?.vatType ?? 'S',
            withoutVat: parseFloat(returnVehicleRentPrice?.deliveryPrice?.withoutVat ?? '0'),
            vat: parseFloat(returnVehicleRentPrice?.deliveryPrice?.vat ?? '0'),
            withVat: parseFloat(returnVehicleRentPrice?.deliveryPrice?.withVat ?? '0'),
          },
          otherPrice: {
            vatType: returnVehicleRentPrice?.otherPrice?.vatType ?? 'S',
            withoutVat: parseFloat(returnVehicleRentPrice?.otherPrice?.withoutVat ?? '0'),
            vat: parseFloat(returnVehicleRentPrice?.otherPrice?.vat ?? '0'),
            withVat: parseFloat(returnVehicleRentPrice?.otherPrice?.withVat ?? '0'),
          },
          chargedFuel: {
            amount: parseFloat(returnVehicleRentPrice?.chargedFuel?.amount ?? '0'),
            price: {
              vatType: returnVehicleRentPrice?.chargedFuel?.price?.vatType ?? 'S',
              withoutVat: parseFloat(returnVehicleRentPrice?.chargedFuel?.price?.withoutVat ?? '0'),
              vat: parseFloat(returnVehicleRentPrice?.chargedFuel?.price?.vat ?? '0'),
              withVat: parseFloat(returnVehicleRentPrice?.chargedFuel?.price?.withVat ?? '0'),
            },
          },
          chargedKilometer: {
            amount: parseFloat(returnVehicleRentPrice?.chargedKilometer?.amount ?? '0'),
            price: {
              vatType: returnVehicleRentPrice?.chargedKilometer?.price?.vatType ?? 'S',
              withoutVat: parseFloat(
                returnVehicleRentPrice?.chargedKilometer?.price?.withoutVat ?? '0'
              ),
              vat: parseFloat(returnVehicleRentPrice?.chargedKilometer?.price?.vat ?? '0'),
              withVat: parseFloat(returnVehicleRentPrice?.chargedKilometer?.price?.withVat ?? '0'),
            },
          },
          coPayment: {
            vatType: returnVehicleRentPrice?.coPayment?.vatType ?? 'S',
            withoutVat: parseFloat(returnVehicleRentPrice?.coPayment?.withoutVat ?? '0'),
            vat: parseFloat(returnVehicleRentPrice?.coPayment?.vat ?? '0'),
            withVat: parseFloat(returnVehicleRentPrice?.coPayment?.withVat ?? '0'),
          },
          contractPenalty: {
            vatType: returnVehicleRentPrice?.contractPenalty?.vatType ?? 'S',
            withoutVat: parseFloat(returnVehicleRentPrice?.contractPenalty?.withoutVat ?? '0'),
            vat: parseFloat(returnVehicleRentPrice?.contractPenalty?.vat ?? '0'),
            withVat: parseFloat(returnVehicleRentPrice?.contractPenalty?.withVat ?? '0'),
          },
          discount: {
            percent: parseFloat(returnVehicleRentPrice?.discount?.percent ?? '0'),
            discount: {
              vatType: returnVehicleRentPrice?.discount?.discount?.vatType ?? 'S',
              withoutVat: parseFloat(returnVehicleRentPrice?.discount?.discount?.withoutVat ?? '0'),
              vat: parseFloat(returnVehicleRentPrice?.discount?.discount?.vat ?? '0'),
              withVat: parseFloat(returnVehicleRentPrice?.discount?.discount?.withVat ?? '0'),
            },
          },
        },
        customPrice: {
          vatType: returnVehicle.customRentPrice?.vatType ?? 'S',
          withoutVat: parseFloat(returnVehicle.customRentPrice?.withoutVat ?? '0'),
          vat: parseFloat(returnVehicle.customRentPrice?.vat ?? '0'),
          withVat: parseFloat(returnVehicle.customRentPrice?.withVat ?? '0'),
        },
        returnAddress: returnVehicle.returnAddress,
        templateId: primaryTemplate?.id,
      }
    : null;

  const defaultValuesFromRent: DeepPartial<ReturnVehicleFormValues> = {
    authorizedPersons: rentVehicle?.authorizedPersons,
    actualMileage: rentVehicle?.actualMileage,
    fuelTank: parseFloat(rentVehicle?.fuelTank ?? '0.5'),
    batteryCharge: parseBatteryCharge(rentVehicle?.batteryCharge) ?? DEFAULT_BATTERY_CHARGE,
    isAddressSame: isNil(rentVehicle?.returnAddress),
    rentAddress: rentVehicle?.rentAddress,
    rentType: rentVehicle?.rentType,
    rentalOrderNote: rentalOrderNote?.text,
    conditionNote: vehicleConditionNote?.text,
    equipment: rentVehicle?.equipment,
    usedPriceGroup: {
      rentalPriceGroupId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId,
      rentalPriceGroupItemId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId,
    },
    isCustomPriceEnabled: isNotNil(rentVehicle?.customRentPrice),
    rentPrice: {
      dailyRate: parseFloat(rentVehicleRentPrice?.dailyRate ?? '0'),
      paidDays: rentVehicleRentPrice?.paidDays,
      freeDailyKilometers: rentVehicleRentPrice?.freeDailyKilometers,
      totalFreeKilometers: rentVehicleRentPrice?.totalFreeKilometers,
      rentalPrice: {
        vatType: rentVehicleRentPrice?.rentalPrice?.vatType ?? 'S',
        withoutVat: parseFloat(rentVehicleRentPrice?.rentalPrice?.withoutVat ?? '0'),
        vat: parseFloat(rentVehicleRentPrice?.rentalPrice?.vat ?? '0'),
        withVat: parseFloat(rentVehicleRentPrice?.rentalPrice?.withVat ?? '0'),
      },
      pricePerKilometer: {
        vatType: rentVehicleRentPrice?.pricePerKilometer?.vatType ?? 'S',
        withoutVat: parseFloat(rentVehicleRentPrice?.pricePerKilometer?.withoutVat ?? '0'),
        vat: parseFloat(rentVehicleRentPrice?.pricePerKilometer?.vat ?? '0'),
        withVat: parseFloat(rentVehicleRentPrice?.pricePerKilometer?.withVat ?? '0'),
      },
      deliveryPrice: {
        vatType: rentVehicleRentPrice?.deliveryPrice?.vatType ?? 'S',
        withoutVat: parseFloat(rentVehicleRentPrice?.deliveryPrice?.withoutVat ?? '0'),
        vat: parseFloat(rentVehicleRentPrice?.deliveryPrice?.vat ?? '0'),
        withVat: parseFloat(rentVehicleRentPrice?.deliveryPrice?.withVat ?? '0'),
      },
      otherPrice: {
        vatType: rentVehicleRentPrice?.otherPrice?.vatType ?? 'S',
        withoutVat: parseFloat(rentVehicleRentPrice?.otherPrice?.withoutVat ?? '0'),
        vat: parseFloat(rentVehicleRentPrice?.otherPrice?.vat ?? '0'),
        withVat: parseFloat(rentVehicleRentPrice?.otherPrice?.withVat ?? '0'),
      },
      discount: {
        percent: parseFloat(rentVehicleRentPrice?.discount?.percent ?? '0'),
        discount: {
          vatType: rentVehicleRentPrice?.discount?.discount?.vatType ?? 'S',
          withoutVat: parseFloat(rentVehicleRentPrice?.discount?.discount?.withoutVat ?? '0'),
          vat: parseFloat(rentVehicleRentPrice?.discount?.discount?.vat ?? '0'),
          withVat: parseFloat(rentVehicleRentPrice?.discount?.discount?.withVat ?? '0'),
        },
      },
      contractPenalty: {vatType: 'S', withoutVat: 0, vat: 0, withVat: 0},
      coPayment: {vatType: 'S', withoutVat: 0, vat: 0, withVat: 0},
      chargedFuel: {amount: 0, price: {vatType: 'S', withoutVat: 0, vat: 0, withVat: 0}},
      chargedKilometer: {amount: 0, price: {vatType: 'S', withoutVat: 0, vat: 0, withVat: 0}},
    },
    customPrice: {
      vatType: rentVehicle?.customRentPrice?.vatType ?? 'S',
      withoutVat: parseFloat(rentVehicle?.customRentPrice?.withoutVat ?? '0'),
      vat: parseFloat(rentVehicle?.customRentPrice?.vat ?? '0'),
      withVat: parseFloat(rentVehicle?.customRentPrice?.withVat ?? '0'),
    },
    returnAddress: rentVehicle?.returnAddress,
    templateId: primaryTemplate?.id,
    actualReturnDate: rentalOrder?.plannedRentalPeriod?.to
      ? parseDate(rentalOrder?.plannedRentalPeriod?.to)
      : undefined,
  };

  return {
    defaultValuesFromRent,
    defaultValues: hasOrderReturnVehicle ? rentalOrderReturnValues : defaultValuesFromRent,
    isLoading,
  };
};
